<script>
import OpenLayersMap from "@/OpenLayersMap.vue";

import { useToast } from "vue-toastification";
import {cloneDeep} from 'lodash';
import { uuid } from 'vue-uuid';
import SearchAddress from "@/SearchAddress.vue";
import DrupalElementSelectUser from "@/DrupalElementSelectUser.vue";

export default {
  components: {
    DrupalElementSelectUser,
    SearchAddress,
    OpenLayersMap,
  },
  props: {
    client_id: Number,
    zoomlevel: {
      type: String,
      default: '13',
    },
    center: {
      type: Object,
      default() {
        return {
          lon: 6.562729,
          lat: 53.215027,
        }
      },
    },
    status_options: null,
    can_assign_users: null,
  },
  async mounted() {
    // Setup toast
    this.toast = useToast()
    // Get CSRF token
    await this.getCSRFtoken();
    this.show_map = true;
  },
  data () {
    return {
      show_map: false,
      toast: null,
      userCSRFToken: '',
      step: 1,
      map_settings: {
        zoomlevel: 6,
        center: {
          lon: 5.366127,
          lat: 52.206774
        },
        mapExtend: null,
      },
      dialog_asset_popup: false,
      dialog_asset_popup_ref: '',
      report_completed: false,
      selected_features: [],
      asset_details: '',
      loadingSubmit: false,
      file_uploader: [],
      keep_me_updated: false,
      values: {
        workorder_name: '',
        workorder_number: '',
        workorder_organisation: null,
        workorder_status: null,
        description: '',
        scheduled_date: null,
        files: []
      },
      is_form_valid: false,
      rules: {
        required: value => !!value || 'Verplicht.',
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Ongeldige e-mail. Vul een geldig e-mailadres in zoals bijvoorbeeld: voorbeeld@lmsapp.nl'
        },
      },
      response_completed: null,
      users: [],
      organisation_options: [],
      organisations_loading: false,
      status_field_disabled: false,
      status_field_description: false,
      status_call: false,
    }
  },
  watch: {
    selected_features: {
      handler(newVal) {
        if (newVal.length > 0) {
          this.getOrganisationOptions();
        }
      },
      deep: true
    }
  },
  computed: {
    file_hint: function() {
      let hint = "";
      hint += "U kunt meerdere bestanden tegelijkertijd uploaden. ";
      hint += "Toestaande extentie's: txt rtf pdf png jpg jpeg mp3 doc docx xls xlsx ppt pptx dwg dgn dxf.";
      return hint;
    },
    selected_features_have_same_type: function() {
      let selected_features = this.selected_features;
      let total = selected_features.length;
      // No features selected yet.
      if (total < 1) {
        return true;
      }
      let bundle = selected_features[0].getProperties().type;
      // Loop over all selected features
      for (let i = 0; i < total; i++) {
        // Check if this feature has the same bundle
        if (selected_features[i].getProperties().type !== bundle) {
          return false;
        }
      }
      return true;
    },
    all_selected_features_have_active_outages: function() {
      let selected_features = this.selected_features;
      let total = selected_features.length;
      let active_outages = 0;

      // No features selected yet.
      if (total < 1) {
        return false;
      }
      // Loop over all selected features
      for (let i = 0; i < total; i++) {
        // Check if this feature has an active outage
        if (selected_features[i].getProperties().outage_status) {
          active_outages++;
        }
      }
      // Check if all selected features have active outages
      return total === active_outages;
    },
    selected_features_out_of_use: function() {
      let selected_features = this.selected_features;
      let total = selected_features.length;
      let out_of_use = 0;

      // No features selected yet.
      if (total < 1) {
        return 0;
      }
      // Loop over all selected features
      for (let i = 0; i < total; i++) {
        // Check if this feature has an active outage
        if (selected_features[i].getProperties().status === 'OutOfUse') {
          out_of_use++;
        }
      }
      return out_of_use;
    },
    all_selected_features_are_out_of_use: function() {
      return this.selected_features.length > 0 && this.selected_features_out_of_use === this.selected_features.length;
    },
    selected_features_client_ids: function() {
      let selected_features = this.selected_features;
      let client_ids = [];
      // Loop over all selected features
      for (let i = 0; i < selected_features.length; i++) {
        if (selected_features[i].getProperties().client) {
          let client_id = selected_features[i].getProperties().client;
          // Check if client_id already exists in the list
          if (!client_ids.includes(client_id)) {
            // Add client_id
            client_ids.push(client_id);
          }
        }
      }
      return client_ids;
    },
    selected_features_have_different_clients_ids: function() {
      return this.selected_features_client_ids.length > 1;
    },
    workorder_status_options: function() {
      // convert status options to v-select items
      let items = [];
      for (const key in this.status_options) {
        items.push({
          'title': this.status_options[key],
          'value': key
        });
      }
      if (items.length === 1) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.values.workorder_status = items[0].value;
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.status_field_disabled = true;
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.status_field_description = 'De status "Maak offerte" geeft de mogelijkheid om zelf of door de aannemer een offerte te laten maken voor de werk order zelf in behandeling wordt genomen. Door te kiezen voor "In uitvoering" wordt de werkorder meteen doorgezet en is er geen offerte nodig.'
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.status_call = true;
      }
      return items
    },
  },
  methods: {
    getAssetPrettyName(asset_bundle) {
      switch (asset_bundle) {
        case 'vri':
          return 'VRI';
        case 'powerbox':
          return 'Voedingskast';
        case 'lightpole':
        default:
          return 'Lichtmast';
      }
    },
    addUser(user) {
      this.users.push(user);
    },
    removeUser(index) {
      this.users.splice(index, 1);
    },
    stepLabel(step_nr) {
      let label = 'Stap ' + step_nr;

      if (this.step > step_nr) {
        label = label + ' (gedaan)';
      }
      else if (this.step === step_nr) {
        label = label + ' (actief)';
      }

      return label;
    },
    StepPrev() {
      this.step--;
      this.$refs.skipLink.focus();
    },
    async StepNext() {
      console.log(this.step);
      this.step++;
      // this.$refs.skipLink.focus();
    },
    StepGoTo(number) {
      this.step = number;
    },
    async getCSRFtoken() {
      // Fetch session token
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'session/token', {
        credentials: "include"
      })
      // Set CSRF token
      this.userCSRFToken = await response.text();
    },
    async showFeatureStatus(featureId) {
      // Show dialog
      this.asset_details = 'Laden..';
      this.dialog_asset_popup = true;
      this.dialog_asset_popup_ref = `status.${featureId}`;

      // Fetch asset details popup
      let url = new URL(import.meta.env.VITE_API_BASEURL + 'ajax/map/'+featureId+'/popup')
      // Fetch results
      const response = await fetch(url)
      // Show error when there is something wrong
      if (!response.ok) {
        this.toast.error("De map items konden niet geladen worden. Foutcode: [" + response.status + "] " + response.statusText);
        return
      }
      this.asset_details = await response.text();
    },
    async goToOutage(asset_id) {
      // Get asset data
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'ajax/asset/asset-data/'+asset_id, {
        credentials: "include"
      })

      if (!response.ok) {
        this.toast.error("De asset kon niet geladen worden. Foutcode: [" + response.status + "] " + response.statusText);
        return
      }

      const asset_data = await response.json();
      if (asset_data.outages.length > 0) {
        // Open outage in new tab
        window.open(asset_data.outages[0].outage_url, '_blank');
      }
    },
    async getOrganisationOptions() {
      const client_id = this.selected_features_client_ids[0];
      this.organisations_loading = true;
      try {
        const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/get-organisation-options/' + client_id, {
          credentials: 'include'
        });

        this.organisation_options = await response.json();
        console.debug(this.organisation_options);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        this.organisations_loading = false;
      }
    },
    selectFeature(feature) {
      // Create a copy. We need this to make sure the Watcher on 'selectedFeatures' in the OpenLayersMap.vue component has a oldValue and newValue.
      // Because: when mutating (rather than replacing) an Object or an Array and watch with deep option, the old value will be the same as new value because they reference the same Object/Array. Vue doesn't keep a copy of the pre-mutate value.
      let selected_features = cloneDeep(this.selected_features);
      // Add feature
      selected_features.push(feature);
      // Override the selected features with the copy
      this.selected_features = selected_features;
      // Reset some form values
      this.values.report_type = '';
      // Go to step 2
      this.StepGoTo(2);
    },
    deselectFeature(featureId) {
      // Create a copy. We need this to make sure the Watcher on 'selectedFeatures' in the OpenLayersMap.vue component has a oldValue and newValue.
      // Because: when mutating (rather than replacing) an Object or an Array and watch with deep option, the old value will be the same as new value because they reference the same Object/Array. Vue doesn't keep a copy of the pre-mutate value.
      let selected_features = cloneDeep(this.selected_features);
      // Find feature in the list of selected features
      let length = selected_features.length
      for (let i = 0; i < length; i++) {
        // Check if this is the feature we want to delete
        if (selected_features[i].getId() === featureId) {
          // Remove the feature from the selected features
          selected_features.splice(i, 1);
          break;
        }
      }
      // Override the selected features with the copy
      this.selected_features = selected_features;
      // Go to step 2
      this.StepGoTo(2);
    },
    getFeatureById(featureId) {
      let selected_features = cloneDeep(this.selected_features);
      let length = selected_features.length
      for (let i = 0; i < length; i++) {
        // Check if this is the feature we want to delete
        if (selected_features[i].getId() === featureId) {
          return selected_features[i];
        }
      }
    },
    async autocompleteLocationSelected(coordinates) {
      // Call function on the map component, go to coordinates.
      this.$refs['map-workorder'].goToCoordinates(coordinates);
      // Go to next step
      this.StepGoTo(2);
    },
    findMyLocation() {
      // Call function on the map component, go to coordinates.
      this.$refs['map-workorder'].geolocationGetLocation();
    },
    async addFile(files) {
      let filesAdded = [];
      for (const file of files) {
        // Convert files to base64 encoding
        let base64 = await this.getBase64(file);
        // Create new file
        let fileAdded = {
          file_name: file.name,
          file_status: 'uploading',
          file_base64: base64,
          file_uuid: uuid.v4(), // Add a UUID we can use to filter/search on
        }
        // Add file to the file list
        this.values.files.push(fileAdded);
        // Add file to newly added files
        filesAdded.push(fileAdded)
      }
      // Clear the file-input, now we have moved to files this.values.files.
      this.file_uploader = [];
      // Start uploading of all newly added files
      for (const fileNew of filesAdded) {
        // Upload file
        await this.uploadFile(fileNew)
      }
    },
    async uploadFile(file) {
      // Upload file to backend
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/lms/file-upload', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.userCSRFToken,
        },
        body: JSON.stringify({
          file_name: file.file_name,
          file_base64: file.file_base64,
          file_uuid: file.file_uuid,
          entity: 'workorder'
        })
      });

      if (response.status !== 201) {
        this.toast.error("Bestand kon niet geupload worden. Foutcode: [" + response.status + "] " + response.statusText);
        // Change the file status
        this.fileErrored(file);
        return
      }

      // Get returned file from the server
      const file_uploaded = await response.json();
      // Update the file status and file ID
      this.fileCompleted(file_uploaded);

      return response
    },
    fileCompleted(file) {
      let length = this.values.files.length
      for (let i = 0; i < length; i++) {
        if (this.values.files[i].file_uuid === file.file_uuid) {
          // Change the status
          this.values.files[i].file_status = 'completed';
          // Set the file_id
          this.values.files[i].file_id = file.file_id;
          // Remove the base64
          delete this.values.files[i].file_base64;
          break;
        }
      }
    },
    fileErrored(file) {
      let length = this.values.files.length
      for (let i = 0; i < length; i++) {
        if (this.values.files[i].file_uuid === file.file_uuid) {
          // Change the status
          this.values.files[i].file_status = 'error';
          break;
        }
      }
    },
    removeFile(file) {
      let length = this.values.files.length
      for (let i = 0; i < length; i++) {
        if (this.values.files[i].file_uuid === file.file_uuid) {
          // Remove the file
          this.values.files.splice(i, 1);
          break;
        }
      }
    },
    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    /**
     * Removes empty values/properties from an object.
     */
    removeEmpty(obj) {
      for (let propName in obj) {
        if (
          obj[propName] === null ||
          obj[propName] === "" ||
          (Array.isArray(obj[propName]) && obj[propName].length === 0)
        ) {
          delete obj[propName];
        }
      }
      return obj
    },
    async sendReportToApi() {
      // Add form values
      let workorder = cloneDeep(this.values);
      workorder = this.removeEmpty(workorder);
      workorder.lightpole_ids = [];
      workorder.users = [];
      // Add selected features
      let length = this.selected_features.length
      for (let i = 0; i < length; i++) {
        workorder.lightpole_ids.push(this.selected_features[i].getId());
      }
      // Add selected users
      for (let user of this.users) {
        workorder.users.push(user.uid);
      }
      // Create workorder API
      return await fetch(import.meta.env.VITE_API_BASEURL + 'api/workorders/create', {
        method: 'POST',
        credentials: "include", // Set to make sure the cookies are send with the request.
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.userCSRFToken,
        },
        body: JSON.stringify(workorder)
      });
    },
    async submitForm() {
      this.$refs['form-submit'].$el.click();
    },
    async submitReport() {
      this.loadingSubmit = true

      let response = await this.sendReportToApi();
      if (!response.ok) {
        this.toast.error("Werkorder kan niet worden aangemaakt. Foutcode: [" + response.status + "] " + response.statusText);
        this.loadingSubmit = false;
        return;
      }

      this.response_completed = await response.json();
      // redirect to newly created workorder
      this.loadingSubmit = false
      if (this.response_completed.redirect_url) {
        window.location.href = this.response_completed.redirect_url;
      }
    },
    closeAssetPopupDialog() {
      this.dialog_asset_popup = false;
      console.debug(this.$refs, '$refs');
      console.debug(this.dialog_asset_popup_ref, 'dialog_asset_popup_ref');
      if (this.$refs[this.dialog_asset_popup_ref] && this.$refs[this.dialog_asset_popup_ref][0]) {
        this.$nextTick(() => this.$refs[this.dialog_asset_popup_ref][0].$el.focus());
      }
    },
    reloadPage() {
      window.location.reload()
    }
  },
}
</script>

<template>
  <a href="#report-outage-content" ref="skipLink" class="focusable skip-link">Door naar storing rapporteren</a>
  <!-- Completed page -->
  <v-container v-if="report_completed" :fluid="true">
    <v-row>
      <v-col>
        <v-card>
          <v-card-text v-if="response_completed" role="alert" aria-live="polite">
            <p>Bedankt voor het melden van de storing.</p>
            <p>Dit is de verwachte hersteldatum(s) van de storing(en):</p>
            <ul v-for="outage in response_completed.outages" :key="outage.asset_id">
              <li>
                {{ getAssetPrettyName(getFeatureById(outage.asset_id).getProperties().type) }} {{ getFeatureById(outage.asset_id).getProperties().name }} <span class="text-repair-date">{{ outage.response.repair_date ? $filters.formatDate(outage.response.repair_date) : 'Onbekend' }}</span>
              </li>
            </ul>
            <p>Als u heeft aangegeven op de hoogte gehouden te willen worden dan krijgt u van de reparatie automatisch een e-mail. Heeft u dit niet gedaan dan kunt u ervan uitgaan dat zonder tegenbericht de storing op bovengenoemde datum is hersteld.</p>
          </v-card-text>
          <v-card-actions>
            <v-btn variant="outlined" color="secondary" @click="reloadPage">Meld nog een storing</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <!-- Report outage page -->
  <v-container v-else :fluid="true">
    <v-row>
      <v-col cols="12" md="6" lg="4">

        <div class="step-pager">
          <div class="step" :class="{'step-active' : step === 1, 'step-done': step > 1}" :aria-current="step === 1 ? 'step' : null"><span class="sr-only">{{ stepLabel(1) }}</span><span aria-hidden="true" class="step-number">1</span></div>
          <div class="step" :class="{'step-active' : step === 2, 'step-done': step > 2}" :aria-current="step === 2 ? 'step' : null"><span class="sr-only">{{ stepLabel(2) }}</span><span aria-hidden="true" class="step-number">2</span></div>
          <div class="step" :class="{'step-active' : step === 3, 'step-done': step > 3}" :aria-current="step === 3 ? 'step' : null"><span class="sr-only">{{ stepLabel(3) }}</span><span aria-hidden="true" class="step-number">3</span></div>
        </div>

        <v-card id="report-outage-content">
          <!-- ----- -->
          <!-- Step 1-->
          <!-- ----- -->
          <v-window v-model="step">
            <v-window-item :value="1">
              <v-card-text class="mb-0">
                <p>Ga naar de locatie van de lichtmast op de kaart. Gebruik het zoekveld om naar het adres op de kaart te gaan of ga zelf inzoomen op de kaart.</p>

                <search-address :client-id="client_id" @location-selected="autocompleteLocationSelected"></search-address>

                <v-btn color="primary" variant="outlined" @click="findMyLocation"><v-icon :start="true">mdi-crosshairs</v-icon> Vind mijn locatie</v-btn>

              </v-card-text>
            </v-window-item>


            <!-- ----- -->
            <!-- Step 2-->
            <!-- ----- -->
            <v-window-item :value="2">
              <v-card-text>

                <p>Selecteer een of meer objecten op de kaart. Klik op een kaart icoontje om het te selecteren.</p>

                <h2>Geselecteerde items</h2>
                <p v-if="selected_features.length < 1">Geen objecten geselecteerd.</p>

                <TransitionGroup name="list" tag="div">
                  <div v-for="(feature) in selected_features" :key="feature.getId()">

                    <v-card variant="tonal" color="secondary" class="mb-2">
                      <v-card-title>
                        <strong>{{ getAssetPrettyName(feature.getProperties().type) }} {{ feature.getProperties().name }}</strong>
                      </v-card-title>
                      <v-card-item class="gap-1">
                        <p>
                          {{ feature.getProperties().street }}, {{ feature.getProperties().city }}
                        </p>
                        <v-btn color="error" class="mb-2 mr-2" prepend-icon="mdi-trash-can-outline" variant="flat" aria-label="Deselecteren" small v-on:click="deselectFeature(feature.getId())">Deselecteren</v-btn>
                        <v-btn color="primary" class="mr-2 mb-2" prepend-icon="mdi-eye" variant="flat" small :ref="'status.' + feature.getId()" v-on:click="showFeatureStatus(feature.getId())">Bekijk status</v-btn>
                      </v-card-item>
                    </v-card>
                  </div>
                </TransitionGroup>

                <v-alert
                  variant="tonal"
                  type="warning"
                  v-if="all_selected_features_are_out_of_use"
                >
                  Alle lichtmasten die u geselecteerd heeft zijn buiten gebruik. Het is niet mogelijk om deze storing(en) te melden.
                </v-alert>

                <v-alert
                  variant="tonal"
                  type="warning"
                  v-if="selected_features_out_of_use > 1 && selected_features_out_of_use < selected_features.length"
                >
                  Een gedeelte van de geselecteerde lichtmasten zijn buiten gebruik. Er wordt geen storing gemeld op deze lichtmasten. De overige storingen zullen nog steeds gemeld worden.
                </v-alert>

                <v-alert
                  variant="tonal"
                  type="error"
                  v-if="selected_features_have_different_clients_ids"
                >
                  Niet alle geselecteerde lichtmasten hebben dezelfde beheerder. U kunt alleen lichtmasten selecteren die bij dezelfde beheerder horen.
                </v-alert>

                <v-alert
                  variant="tonal"
                  type="error"
                  v-if="!selected_features_have_same_type"
                >
                  U kunt niet verschillende typen objecten selecteren. Selecteer alleen lichtmasten of voedingskasten.
                </v-alert>


              </v-card-text>
            </v-window-item>


            <!-- ----- -->
            <!-- Step 3-->
            <!-- ----- -->
            <v-window-item :value="3">
              <v-card-text>
                <v-form v-if="selected_features.length" @submit.prevent="submitReport" v-model="is_form_valid" ref="form">

                  <!-- create name input -->
                  <v-text-field v-model="values.workorder_name" aria-label="Werkorder naam" :rules="[rules.required]">
                    <template #label>
                      Werkorder naam <span class="red--text"><strong>*</strong></span>
                    </template>
                  </v-text-field>

                  <v-text-field v-model="values.workorder_number" label="Werkorder nummer"></v-text-field>

                  <v-select
                    :disabled="organisation_options.length === 0"
                    :loading="organisations_loading"
                    :items="organisation_options"
                    v-model="values.workorder_organisation"
                    clearable
                    item-title="text"
                    item-value="value"
                    label="Organisatie"></v-select>
                  <!-- select field shows available status options -->
                  <v-select
                    :items="workorder_status_options"
                    v-model="values.workorder_status"
                    aria-label="Werkorder status"
                    :rules="[rules.required]">
                    <template #label>
                      Werkorder status <span class="red--text"><strong>*</strong></span>
                    </template>
                  </v-select>

                  <div v-if="values.workorder_status !== 'CreateQuotation'">
                    <drupal-element-select-user
                      :users="users"
                      @user-added="addUser"
                      @user-remove="removeUser"
                      label="Selecteer gebruiker"
                      user_empty_text="Geen gebruikers geselecteerd"
                      :user_cardinality="-1"></drupal-element-select-user>
                  </div>

                  <v-textarea
                    label="Omschrijving"
                    variant="outlined"
                    v-model="values.description"
                  ></v-textarea>

                  <!-- date field for scheduled date -->
                  <v-date-input
                    v-if="!all_selected_features_have_active_outages"
                    clearable
                    placeholder="Kies een datum"
                    v-model="values.scheduled_date"
                    label="Geplande datum"></v-date-input>

                  <v-file-input
                    label="Upload foto's en bestanden"
                    multiple
                    prepend-icon=""
                    prepend-inner-icon="mdi-paperclip"
                    variant="outlined"
                    :hint="file_hint"
                    persistent-hint
                    accept="image/*"
                    @update:modelValue="addFile"
                    v-model="file_uploader"
                  ></v-file-input>

                  <v-list v-if="values.files && values.files.length > 0" lines="one">
                    <v-list-item
                      v-for="file in values.files"
                      :key="file.file_name"
                    >
                      <p>
                        <v-progress-circular
                          v-if="file.file_status === 'uploading'"
                          size="24"
                          color="warning"
                          indeterminate
                        ></v-progress-circular>
                        <v-icon v-if="file.file_status === 'completed'" size="large" color="success" aria-label="Bestand geüpload">mdi-check-circle</v-icon>
                        <v-icon color="error" size="large" @click="removeFile(file)" aria-label="Verwijderen">mdi-trash-can</v-icon>
                        {{ file.file_name }}
                      </p>
                      <v-alert
                        v-if="file.file_status === 'error'"
                        density="compact"
                        type="error"
                        text="Bestand kon niet geupload worden. Verwijder dit bestand uit de lijst en probeer het opnieuw."
                      ></v-alert>

                      <v-divider></v-divider>
                    </v-list-item>
                  </v-list>

                  <p class="mt-1 text-red-accent-4 font-italic">Velden met een "*" zijn verplicht.</p>

                  <v-btn v-show="false" type="submit" ref="form-submit" :disabled="!is_form_valid || loadingSubmit" :loading="loadingSubmit" class="mt-2">Werkorder aanmaken</v-btn>

                </v-form>

              </v-card-text>
            </v-window-item>
          </v-window>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn v-if="step > 1" variant="outlined" color="secondary" @click="StepPrev">Vorige</v-btn>
            <v-spacer></v-spacer>
            <template v-if="step === 3">
              <v-btn type="submit" variant="flat" size="large" :disabled="!is_form_valid || loadingSubmit" :loading="loadingSubmit" class="mt-2" @click.prevent="submitForm">Werkorder aanmaken</v-btn>
            </template>
            <template v-else-if="step === 2">
              <v-btn variant="flat" :disabled="selected_features.length < 1 || selected_features_have_different_clients_ids || all_selected_features_are_out_of_use || !selected_features_have_same_type" size="large" @click="StepNext">Volgende</v-btn>
            </template>
            <template v-else>
              <v-btn v-if="step < 3" variant="flat" size="large" @click="StepNext">Volgende</v-btn>
            </template>
          </v-card-actions>
        </v-card>

      </v-col>
      <v-col cols="12" md="6" lg="8">

        <v-skeleton-loader height="600" class="map-loader" v-if="!show_map"></v-skeleton-loader>
        <open-layers-map
          ref="map-workorder"
          v-if="show_map"
          :client-id="client_id"
          :features-url="'ajax/map/workorders-create'"
          :map-settings="map_settings"
          :selected-features="selected_features"
          @selectFeature="selectFeature($event)"
          @deselectFeature="deselectFeature($event)"
        ></open-layers-map>
      </v-col>
    </v-row>
  </v-container>

  <v-dialog
    v-model="dialog_asset_popup"
    ref="dialogAssetPopup"
    role="dialog"
    aria-label="Statusdialoog van object"
    width="auto"
    autofocus
  >
    <v-card>
      <v-card-text>
        <div v-html="asset_details"></div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="closeAssetPopupDialog(dialog_asset_popup_ref)">Sluiten</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<style lang="scss">

a:focus {

}
.skip-link {
  white-space: nowrap;
  margin: 1em auto;
  top: 0;
  position: fixed;
  left: 50%;
  margin-left: -72px;
  opacity: 0;
}
.skip-link:focus {
  opacity: 1;
  background-color: white;
  color: black;
  padding: 0.5em;
  border: 1px solid black;
}

.map-loader .v-skeleton-loader__image {
  height: 600px;
}

.v-btn.bg-warning {
  .v-btn__content, .v-btn__prepend {
    color: black;
  }
}
.v-alert.text-warning {
  .v-alert__content, .v-alert__prepend {
    color: #A24C06 !important;
  }
}

[data-v-app] {
  *:focus:not([type=radio]),
  .ui-dialog *:focus:not([type=radio]),
  a:hover,
  a:focus,
  .link:hover,
  .link:focus {
    box-shadow: 0 0 0 var(--focus-border-offset-size) var(--color-white), 0 0 0 calc(var(--focus-border-size) + var(--focus-border-offset-size)) #000000 !important;
  }
}

</style>
<style scoped lang="scss">
/* COLORS */
$nli-pink: #DF0080;
$purple: #6A5FF4;
$hot-pink: #FE405B;
$green: #187b53;
$light-green: #9fff75;
$yellow: #F8D300;
$dark-red: #AC0000;
$blue: #2D9EE0;
$accessibility-blue: #216C99;
$dark-blue: #0e3548;
$red: #d9534f;
$orange: #f3a50f;
$white: #fff;
$black: #000000;


/* FONT */
$font-montserrat: "Montserrat", sans-serif;
$font-weight-bold: 700;

:root {
  --v-theme-primary: $nli-pink;
}



.text-repair-date {
  color: $accessibility-blue;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}


.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.required label::after {
  content: "*";
}


.step-pager {
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  margin: 0 auto 1.5em;

  &::before {
    z-index: 0;
    content: "";
    display: block;
    position: absolute;
    height: 2px;
    top: calc(50% - 1px);
    background: $accessibility-blue;
    width: calc(100% - 20px);
  }

  .step {
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;
    border: 0 solid $accessibility-blue;
    color: $accessibility-blue;
    background-color: $white;
    border-radius: 50%;
    min-width: 25px;
    min-height: 25px;
    line-height: 20px;
    font-size: 16px;

    &-active {
      color: $nli-pink;
      border: 2px solid $nli-pink;
      background-color: $white;
    }

    &-number {
      font-family: $font-montserrat;
      font-weight: 800;
      line-height: 1;
      vertical-align: middle;
    }
  }
}
</style>
